import { FormikValues, useFormik } from 'formik'
import { FC, useEffect } from 'react'
import { Spinner } from 'react-bootstrap'
import * as Yup from 'yup'
import { useBookingFormState } from '../core/FormStateProvider'
import { useSelectTreatmentModal } from '../core/_ModalProvider'
import { SurveySuccessModal } from './SurveySuccessModal'

const surveyFormSchemes = Yup.object().shape({
  therapist_name: Yup.string().required('Therapist Name is required'),
  room_number: Yup.string().required('Room No. is required'),
  service_rating: Yup.string().required('Service rating is required'),
  cleanliness_rating: Yup.string().required('Cleanliness rating is required'),
  therapist_skill_rating: Yup.string().required('Therapist Skill rating is required'),
  procedure_explanation_rating: Yup.string().required('Procedure Explanation rating is required'),
  punctuality_rating: Yup.string().required('Punctuality rating is required'),
  product_quality_rating: Yup.string().required('Product Quality rating is required'),
  guest_comments: Yup.string().required('Guest Comments are required'),
})

const surveyFormInitialValues = {
  therapist_name: '',
  room_number: '',
  service_rating: '',
  cleanliness_rating: '',
  therapist_skill_rating: '',
  procedure_explanation_rating: '',
  punctuality_rating: '',
  product_quality_rating: '',
  guest_comments: '',
}

type Props = {
  outlet_id: string
  handleSubmit: (body: any) => void
}

const SurveyForm: FC<Props> = ({outlet_id, handleSubmit}) => {
  const {isLoading, setIsLoading} = useBookingFormState()
  const {showSuccessModal, closeSuccessModal} = useSelectTreatmentModal()

  const onSubmit = (values: FormikValues) => {
    const body = {
      outlet_guid: outlet_id,
      therapist_name: values.therapist_name,
      room_number: values.room_number,
      ratings: {
        service: values.service_rating,
        cleanliness: values.cleanliness_rating,
        therapist_skill: values.therapist_skill_rating,
        procedure_explanation: values.procedure_explanation_rating,
        punctuality: values.punctuality_rating,
        product_quality: values.product_quality_rating,
      },
      comments: values.guest_comments,
    }
    setIsLoading(true)
    handleSubmit(body)
  }

  const {
    values,
    handleChange,
    handleBlur,
    handleReset,
    handleSubmit: handleFormSubmit,
    isValid,
    touched,
    errors,
  } = useFormik({
    initialValues: surveyFormInitialValues,
    validationSchema: surveyFormSchemes,
    validateOnMount: true,
    validateOnChange: true,
    validateOnBlur: true,
    onSubmit,
  })

  useEffect(() => {
    if (!showSuccessModal) {
      handleReset(surveyFormInitialValues)
    }
  }, [showSuccessModal, handleReset])

  const treatmentAspects = [
    {key: 'service_rating', label: 'Service'},
    {key: 'cleanliness_rating', label: 'Cleanliness'},
    {key: 'therapist_skill_rating', label: 'Therapist Skill'},
    {key: 'procedure_explanation_rating', label: 'Procedure Explanation'},
    {key: 'punctuality_rating', label: 'Punctuality'},
    {key: 'product_quality_rating', label: 'Product Quality'},
  ]

  return (
    <>
      <form onSubmit={handleFormSubmit} style={{padding: '2rem'}}>
        {/* Header */}
        <div className='text-center mb-6'>
          <h1 className='fw-bold text-gray-800 mb-3 d-flex align-items-center justify-content-center gap-2' style={{fontSize: '1.75rem'}}>
            <i className='fas fa-star text-warning'></i>
            POST-TREATMENT SURVEY
          </h1>
          <p className='text-gray-600 mb-0' style={{fontSize: '0.95rem'}}>
            Dear Guest, please take a moment to fill out this card to help us improve our services.{' '}
            <span className='text-muted'>(1 = Lowest Score, 5 = Highest Score)</span>
          </p>
        </div>

        <div className='row'>
          {/* Therapist and Room Information */}
          <div className='col-12 col-md-6 mb-5'>
            <label htmlFor='therapist_name' className='form-label fw-semibold text-gray-700 mb-2 required'>
              Therapist Name
            </label>
            <input
              type='text'
              id='therapist_name'
              className={`form-control form-control-solid ${
                touched.therapist_name && errors.therapist_name ? 'is-invalid' : ''
              }`}
              placeholder='To be filled by Spa / Guest'
              value={values.therapist_name}
              onChange={handleChange('therapist_name')}
              onBlur={handleBlur('therapist_name')}
              disabled={!outlet_id}
              style={{backgroundColor: '#f5f8fa'}}
            />
            {touched.therapist_name && errors.therapist_name && (
              <div className='fv-plugins-message-container'>
                <div className='fv-help-block'>
                  <span role='alert' className='text-danger'>
                    {errors.therapist_name}
                  </span>
                </div>
              </div>
            )}
          </div>
          <div className='col-12 col-md-6 mb-5'>
            <label htmlFor='room_number' className='form-label fw-semibold text-gray-700 mb-2 required'>
              Room No.
            </label>
            <input
              type='text'
              id='room_number'
              className={`form-control form-control-solid ${
                touched.room_number && errors.room_number ? 'is-invalid' : ''
              }`}
              placeholder='Room No.'
              value={values.room_number}
              onChange={handleChange('room_number')}
              onBlur={handleBlur('room_number')}
              disabled={!outlet_id}
              style={{backgroundColor: '#f5f8fa'}}
            />
            {touched.room_number && errors.room_number && (
              <div className='fv-plugins-message-container'>
                <div className='fv-help-block'>
                  <span role='alert' className='text-danger'>
                    {errors.room_number}
                  </span>
                </div>
              </div>
            )}
          </div>

          {/* Service Rating Section */}
          <div className='col-12 mb-6'>
            <h4 className='fw-bold text-gray-800 mb-4' style={{fontSize: '1.25rem'}}>
              Service Rating (1-5)
            </h4>
            <div className='table-responsive'>
              <table className='table table-bordered align-middle mb-0' style={{borderCollapse: 'separate', borderSpacing: 0}}>
                <thead>
                  <tr className='fw-bold text-gray-700' style={{backgroundColor: '#f8f9fa'}}>
                    <th style={{width: '40%', padding: '12px', borderBottom: '2px solid #dee2e6'}}>
                      TREATMENT ASPECT
                    </th>
                    <th className='text-center' style={{width: '12%', padding: '12px', borderBottom: '2px solid #dee2e6'}}>
                      1
                    </th>
                    <th className='text-center' style={{width: '12%', padding: '12px', borderBottom: '2px solid #dee2e6'}}>
                      2
                    </th>
                    <th className='text-center' style={{width: '12%', padding: '12px', borderBottom: '2px solid #dee2e6'}}>
                      3
                    </th>
                    <th className='text-center' style={{width: '12%', padding: '12px', borderBottom: '2px solid #dee2e6'}}>
                      4
                    </th>
                    <th className='text-center' style={{width: '12%', padding: '12px', borderBottom: '2px solid #dee2e6'}}>
                      5
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {treatmentAspects.map((aspect, index) => (
                    <>
                      <tr
                        key={aspect.key}
                        style={{
                          backgroundColor: index % 2 === 0 ? '#ffffff' : '#f8f9fa',
                        }}
                      >
                        <td className='fw-semibold text-gray-700' style={{padding: '14px 12px'}}>
                          {aspect.label}
                        </td>
                        {[1, 2, 3, 4, 5].map((rating) => (
                          <td key={rating} className='text-center' style={{padding: '14px 12px'}}>
                            <label className='form-check form-check-custom form-check-solid d-flex justify-content-center'>
                              <input
                                className='form-check-input'
                                type='radio'
                                name={aspect.key}
                                value={rating.toString()}
                                checked={values[aspect.key as keyof typeof values] === rating.toString()}
                                onChange={handleChange(aspect.key)}
                                onBlur={handleBlur(aspect.key)}
                                style={{
                                  width: '20px',
                                  height: '20px',
                                  cursor: 'pointer',
                                }}
                              />
                            </label>
                          </td>
                        ))}
                      </tr>
                      {touched[aspect.key as keyof typeof touched] &&
                        errors[aspect.key as keyof typeof errors] && (
                          <tr key={`${aspect.key}-error`}>
                            <td colSpan={6} className='text-danger' style={{padding: '8px 12px', fontSize: '0.875rem'}}>
                              {errors[aspect.key as keyof typeof errors]}
                            </td>
                          </tr>
                        )}
                    </>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Guest Comments Section */}
          <div className='col-12 mb-5'>
            <h4 className='fw-bold text-gray-800 mb-3' style={{fontSize: '1.25rem'}}>
              Guest Comments
            </h4>
            <label htmlFor='guest_comments' className='form-label text-muted mb-2 required' style={{fontSize: '0.9rem'}}>
              Additional Comments / Suggestions
            </label>
            <textarea
              id='guest_comments'
              className={`form-control form-control-solid ${
                touched.guest_comments && errors.guest_comments ? 'is-invalid' : ''
              }`}
              rows={6}
              placeholder='Share your feedback for our service improvement.'
              value={values.guest_comments}
              onChange={handleChange('guest_comments')}
              onBlur={handleBlur('guest_comments')}
              disabled={!outlet_id}
              style={{
                backgroundColor: '#f5f8fa',
                resize: 'vertical',
              }}
            />
            {touched.guest_comments && errors.guest_comments && (
              <div className='fv-plugins-message-container'>
                <div className='fv-help-block'>
                  <span role='alert' className='text-danger'>
                    {errors.guest_comments}
                  </span>
                </div>
              </div>
            )}
          </div>

          {/* Submit Button */}
          <div className='col-12'>
            <button
              className='btn btn-md w-100 fw-bold'
              style={{
                backgroundColor: '#28a745',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                padding: '12px',
                fontSize: '1rem',
              }}
              type='submit'
              disabled={!isValid || isLoading || !outlet_id}
            >
              {isLoading ? (
                <Spinner animation='border' color='white' size='sm' />
              ) : (
                'Submit Survey (Finish)'
              )}
            </button>
          </div>
        </div>
        <SurveySuccessModal
          onReserve={() => {
            handleReset(surveyFormInitialValues)
            closeSuccessModal()
          }}
        />
      </form>
    </>
  )
}

export default SurveyForm
